<template>
  <div class="performance-editor">
    <!-- 编辑器容器 -->
    <div ref="editorContainer" class="editor-wrapper"></div>

    <!-- 底部操作按钮 -->
    <div class="editor-actions" v-if="showActions">
      <button @click="handleGetContent" class="action-btn">获取内容</button>
      <button @click="handleClearContent" class="action-btn">清空内容</button>
      <button @click="handleResetTemplate" class="action-btn">重置模板</button>
      <button @click="handleToggleDisable" class="action-btn">
        {{ isDisabled ? "启用编辑" : "禁用编辑" }}
      </button>
    </div>
  </div>
</template>

<script>
import WangEditor from "wangeditor";

export default {
  name: "PerformanceEditor",
  props: {
    // 初始内容
    initialContent: {
      type: String,
      default: "",
    },
    // 是否禁用编辑
    disabled: {
      type: Boolean,
      default: false,
    },
    // 是否显示操作按钮
    showActions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      editor: null,
      isDisabled: this.disabled,
      // 绩效考核模板内容
      templateContent: `
        <h2>本次绩效考核周期表现的亮点</h2>
        <ol>
          <li>完成结果和过程中的亮点有哪些？</li>
          <li>面谈所有人员达成共识的优势</li>
        </ol>
        <p>&nbsp;</p>
        <h2>本次绩效考核周期待提升点</h2>
        <ol>
          <li>完成不足的点分别有哪些？</li>
          <li>面谈所有人员达成共识的提升点</li>
        </ol>
        <p>&nbsp;</p>
        <h2>差距分析</h2>
        <ol>
          <li>目标和实际完成情况分别是多少？差距有多少？</li>
          <li>分析原因</li>
        </ol>
        <p>&nbsp;</p>
        <h2>改进计划</h2>
        <ol>
          <li>改进计划的目标有哪些？</li>
          <li>上级如何配合跟进和提供所需资源？</li>
          <li>员工在改进计划中的策略及行动是什么？</li>
        </ol>
      `,
    };
  },
  watch: {
    disabled(newVal) {
      this.isDisabled = newVal;
      this.updateEditorStatus();
    },
    // 监听初始内容变化，更新编辑器内容
    initialContent(newVal) {
      if (this.editor && newVal) {
        this.editor.txt.html(newVal);
      }
    },
  },
  mounted() {
    this.initEditor();
  },
  beforeDestroy() {
    // 销毁编辑器实例，避免内存泄漏
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },
  methods: {
    // 初始化编辑器
    initEditor() {
      // 创建编辑器实例
      this.editor = new WangEditor(this.$refs.editorContainer);

      // 配置编辑器
      this.configEditor();

      // 监听内容变化
      this.editor.config.onchange = (html) => {
        this.$emit("content-change", html);
      };

      // 创建编辑器
      this.editor.create();

      // 设置初始内容（必须在create之后）
      const content = this.initialContent || this.templateContent;
      this.editor.txt.html(content);

      // 设置禁用状态
      this.updateEditorStatus();
    },

    // 配置编辑器
    configEditor() {
      // 配置菜单栏
      this.editor.config.menus = [
        "head", // 标题
        "bold", // 粗体
        "fontSize", // 字号
        "fontName", // 字体
        "italic", // 斜体
        "underline", // 下划线
        "strikeThrough", // 删除线
        "foreColor", // 文字颜色
        "justify", // 对齐方式
        "undo", // 撤销
        "redo", // 重做
      ];

      // 配置 placeholder
      this.editor.config.placeholder = "请输入绩效考核内容...";

      // 配置编辑器高度
      this.editor.config.height = "700px";
    },

    // 更新编辑器禁用状态
    updateEditorStatus() {
      if (this.editor) {
        this.isDisabled ? this.editor.disable() : this.editor.enable();
      }
    },

    // 获取编辑器内容
    getContent() {
      return this.editor ? this.editor.txt.html() : "";
    },

    // 设置编辑器内容
    setContent(html) {
      if (this.editor) {
        this.editor.txt.html(html);
      }
    },

    // 清空内容
    clearContent() {
      if (this.editor) {
        this.editor.txt.clear();
      }
    },

    // 重置为模板内容
    resetTemplate() {
      this.setContent(this.templateContent);
    },

    // 切换禁用状态
    toggleDisable() {
      this.isDisabled = !this.isDisabled;
      this.updateEditorStatus();
      this.$emit("disabled-change", this.isDisabled);
    },

    // 处理获取内容按钮点击
    handleGetContent() {
      const content = this.getContent();
      this.$emit("get-content", content);
    },

    // 处理清空内容按钮点击
    handleClearContent() {
      this.clearContent();
      this.$emit("content-cleared");
    },

    // 处理重置模板按钮点击
    handleResetTemplate() {
      this.resetTemplate();
      this.$emit("template-reset");
    },

    // 处理切换禁用状态按钮点击
    handleToggleDisable() {
      this.toggleDisable();
    },
  },
};
</script>

<style scoped>
.performance-editor {
  width: 100%;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  overflow: hidden;
  box-sizing: border-box;
}
.editor-wrapper {
  min-height: 700px;
  padding: 30px 10px 10px;
  box-sizing: border-box;
}
.editor-actions {
  display: flex;
  padding: 10px;
  background-color: #f5f7fa;
  border-top: 1px solid #e5e6eb;
  gap: 10px;
}
.action-btn {
  padding: 6px 12px;
  background-color: #4e5969;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}
.action-btn:hover {
  background-color: #272e3b;
}
.action-btn:active {
  background-color: #1d2129;
}
</style>
